import Bugsnag from '@bugsnag/js'
import BugsnagPluginVue from '@bugsnag/plugin-vue'
import BugsnagPerformance from '@bugsnag/browser-performance'
import type { App } from 'vue'

export interface BugsnagConfig {
  apiKey: string
  environment: string
  enabled: boolean
  releaseStage: string
  enabledReleaseStages: string[]
  appType: string
  appVersion: string
}

export class BugsnagService {
  private static instance: BugsnagService
  private isInitialized = false
  private config: BugsnagConfig
  private originalConsole: {
    log: typeof console.log
    warn: typeof console.warn
    error: typeof console.error
    info: typeof console.info
  }

  private constructor() {
    // Store original console methods
    this.originalConsole = {
      log: console.log.bind(console),
      warn: console.warn.bind(console),
      error: console.error.bind(console),
      info: console.info.bind(console)
    }

    this.config = {
      apiKey: import.meta.env.VITE_BUGSNAG_API_KEY || '',
      environment: import.meta.env.VITE_APP_ENV || 'development',
      enabled: import.meta.env.VITE_BUGSNAG_ENABLED === 'true',
      releaseStage: import.meta.env.VITE_RELEASE_STAGE || import.meta.env.VITE_APP_ENV || 'development',
      enabledReleaseStages: ['production', 'staging', 'integration', 'local'],
      appType: 'bitshuttle-signage-portal-frontend',
      appVersion: import.meta.env.PACKAGE_VERSION || '1.0.0'
    }
  }

  public static getInstance(): BugsnagService {
    if (!BugsnagService.instance) {
      BugsnagService.instance = new BugsnagService()
    }
    return BugsnagService.instance
  }

  public init(app: App): void {
    if (this.isInitialized) {
      return
    }
    if (!this.config.enabled || !this.config.apiKey) {
      console.info('[Bugsnag] Disabled or no API key provided')
      return
    }

    try {
      // Initialize Bugsnag
      Bugsnag.start({
        apiKey: this.config.apiKey,
        releaseStage: this.config.releaseStage,
        enabledReleaseStages: this.config.enabledReleaseStages,
        appType: this.config.appType,
        appVersion: this.config.appVersion,
        plugins: [new BugsnagPluginVue()],
        collectUserIp: false,
        maxBreadcrumbs: 50,
        autoTrackSessions: true,
        autoDetectErrors: true,
        onError: (event) => {
          // Add custom metadata to all reports
          event.addMetadata('app', {
            environment: this.config.environment,
            releaseStage: this.config.releaseStage,
            version: this.config.appVersion,
            buildTime: new Date().toISOString()
          })
          return true
        }
      })

      // Initialize Performance monitoring
      BugsnagPerformance.start({ apiKey: this.config.apiKey })

      // Install Vue plugin
      const bugsnagVue = Bugsnag.getPlugin('vue')
      if (bugsnagVue) {
        app.use(bugsnagVue)
      }

      // Override console methods to send logs to Bugsnag
      this.interceptConsoleLogs()

      this.isInitialized = true
      console.info(`[Bugsnag] Initialized successfully for ${this.config.releaseStage} release stage (environment: ${this.config.environment})`)
    } catch (error) {
      console.error('[Bugsnag] Failed to initialize:', error)
    }
  }

  private interceptConsoleLogs(): void {
    const self = this

    // Override console.log
    console.log = (...args: any[]) => {
      self.originalConsole.log(...args)
      self.sendLogToBugsnag('log', args)
    }

    // Override console.warn
    console.warn = (...args: any[]) => {
      self.originalConsole.warn(...args)
      self.sendLogToBugsnag('warn', args)
    }

    // Override console.error
    console.error = (...args: any[]) => {
      self.originalConsole.error(...args)
      self.sendLogToBugsnag('error', args)
    }

    // Override console.info
    console.info = (...args: any[]) => {
      self.originalConsole.info(...args)
      self.sendLogToBugsnag('info', args)
    }
  }

  private sendLogToBugsnag(level: 'log' | 'warn' | 'error' | 'info', args: any[]): void {
    if (!this.isInitialized || !this.config.enabled) {
      return
    }

    try {
      const message = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' ')

    // Create breadcrumb for all console logs uncomment if need can consume quota
    //   Bugsnag.leaveBreadcrumb(`Console ${level}`, {
    //     message,
    //     timestamp: new Date().toISOString(),
    //     level
    //   })

      // For errors and warnings, also notify Bugsnag
      if (level === 'error') {
        const error = new Error(`Console Error: ${message}`)
        error.name = 'ConsoleError'
        Bugsnag.notify(error, (event) => {
          event.addMetadata('console', {
            level,
            originalArgs: args,
            timestamp: new Date().toISOString()
          })
          event.severity = 'error'
        })
      } else if (level === 'warn') {
        const warning = new Error(`Console Warning: ${message}`)
        warning.name = 'ConsoleWarning'
        Bugsnag.notify(warning, (event) => {
          event.addMetadata('console', {
            level,
            originalArgs: args,
            timestamp: new Date().toISOString()
          })
          event.severity = 'warning'
        })
      }
    } catch (error) {
      // Fallback to original console to avoid infinite loops
      this.originalConsole.error('[Bugsnag] Error processing console log:', error)
    }
  }

  public notify(error: Error | string, metadata?: Record<string, any>): void {
    if (!this.isInitialized || !this.config.enabled) {
      return
    }

    const errorObj = typeof error === 'string' ? new Error(error) : error
    Bugsnag.notify(errorObj, (event) => {
      event.addMetadata('custom', metadata || {})
      event.addMetadata('environment', {
        name: this.config.environment,
        releaseStage: this.config.releaseStage,
        timestamp: new Date().toISOString()
      })
    })
  }

  public addMetadata(section: string, data: Record<string, any>): void {
    if (!this.isInitialized || !this.config.enabled) {
      return
    }

    Bugsnag.addMetadata(section, data)
  }

  public setUser(id: string, email?: string, name?: string): void {
    if (!this.isInitialized || !this.config.enabled) {
      return
    }

    Bugsnag.setUser(id, email, name)
  }

  public leaveBreadcrumb(message: string, metadata?: Record<string, any>): void {
    if (!this.isInitialized || !this.config.enabled) {
      return
    }

    Bugsnag.leaveBreadcrumb(message, metadata)
  }

  public getConfig(): BugsnagConfig {
    return { ...this.config }
  }

  public isEnabled(): boolean {
    return this.isInitialized && this.config.enabled
  }
}

// Export singleton instance
export const bugsnagService = BugsnagService.getInstance()

// Export Bugsnag instance for direct access if needed
export default Bugsnag
